import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import { classroomsAPI } from '../../api';
import { periodsAPI } from '../../api/periodsAPI';
import dailyReportingAPI from '../../services/dailyReportingAPI';
import ClassAnalytics from './ClassAnalytics';

export default function ClassTrackingDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading: tenantLoading, currentSchool } = useTenant();
  
  // State management
  const [selectedClass, setSelectedClass] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [dateRange, setDateRange] = useState('thisMonth');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAnalyticsTab, setSelectedAnalyticsTab] = useState('class'); // 'class' or period ID

  // Use ref to track auto-selection to prevent dependency loops
  const hasAutoSelectedRef = useRef(false);

  console.log('ClassTrackingDashboard render - classes:', availableClasses.length, 'selectedClass:', selectedClass?.id, 'loading:', loading, 'tenantLoading:', tenantLoading);

  const getDateRangeValues = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'today':
        return {
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
      case 'thisWeek':
        // This week: Sunday to Saturday (current week)
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Go to Sunday
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Go to Saturday
        return {
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: endOfWeek.toISOString().split('T')[0]
        };
      case 'thisMonth':
        // This month: 1st day to last day of current month
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: endOfMonth.toISOString().split('T')[0]
        };
      case 'custom':
        return customDateRange;
      default:
        // Default to this month if no valid option
        const defaultStartOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const defaultEndOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          startDate: defaultStartOfMonth.toISOString().split('T')[0],
          endDate: defaultEndOfMonth.toISOString().split('T')[0]
        };
    }
  }, [dateRange, customDateRange]);

  // Load classes on component mount
  useEffect(() => {
    const loadClasses = async () => {
      if (tenantLoading || !currentSchool?.id) {
        console.log('Skipping class load - tenant loading or no school');
        return;
      }

      try {
        setLoading(true);
        console.log('Loading classes for school:', currentSchool.id);
        
        const token = await getAccessTokenSilently();
        const schoolId = currentSchool?.unique_id || currentSchool?.unique_id;
        // Use adminView=true for analytics to show ALL classes in the school, not just teacher-assigned ones
        const classesData = await classroomsAPI.getAllClassrooms(token, null, schoolId, true);
        
        console.log('Loaded classes for analytics (all classes in school):', classesData);
        setAvailableClasses(classesData || []);
        
        // Auto-select first class if available and haven't auto-selected before
        if (classesData && classesData.length > 0 && !selectedClass && !hasAutoSelectedRef.current) {
          console.log('Auto-selecting first class:', classesData[0].name);
          setSelectedClass(classesData[0]);
          hasAutoSelectedRef.current = true;
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading classes:', err);
        setError('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, tenantLoading, currentSchool?.id]);

  // Load periods when class changes
  useEffect(() => {
    const loadPeriods = async () => {
      if (!selectedClass?.id) {
        setAvailablePeriods([]);
        return;
      }

      try {
        console.log('Loading periods for class:', selectedClass.id);
        const token = await getAccessTokenSilently();
        const periodsData = await periodsAPI.getPeriodsByClassroom(selectedClass.id, token);
        
        console.log('Loaded periods:', periodsData);
        setAvailablePeriods(periodsData || []);
        
      } catch (err) {
        console.error('Error loading periods:', err);
        setAvailablePeriods([]);
      }
    };

    loadPeriods();
  }, [selectedClass?.id, getAccessTokenSilently]);

  // Load attendance data when class or date range changes (but NOT when period tab changes)
  useEffect(() => {
    const loadAttendanceData = async () => {
      if (!selectedClass) return;

      try {
        setLoading(true);
        setError(null);

        const { startDate, endDate } = getDateRangeValues();
        console.log('Loading attendance data for class:', selectedClass.id, 'Date range:', startDate, 'to', endDate);

        const token = await getAccessTokenSilently();
        
        // Get the school ID for the API call
        const schoolId = currentSchool?.unique_id || currentSchool?.unique_id;
        if (!schoolId) {
          throw new Error('School ID not available');
        }
        
        // Get ALL attendance data for the class (no period filtering - we'll filter on frontend)
        const response = await dailyReportingAPI.getClassAnalytics(token, {
          classroomId: selectedClass.id,
          startDate,
          endDate,
          schoolId
          // Removed periodId filter - we'll filter on frontend for better performance
        });

        console.log('Class attendance data loaded from daily reporting service (all periods):', response);
        setAttendanceData(response);

      } catch (err) {
        console.error('Error loading attendance data:', err);
        setError('Failed to load attendance data');
        setAttendanceData(null);
      } finally {
        setLoading(false);
      }
    };

    loadAttendanceData();
  }, [selectedClass, dateRange, customDateRange, getAccessTokenSilently, getDateRangeValues, currentSchool?.unique_id]); // Removed selectedAnalyticsTab from dependencies and duplicate dependency

  const handleClassSelect = (classItem) => {
    console.log('handleClassSelect called with:', classItem);
    console.log('Current selectedClass:', selectedClass);
    setSelectedClass(classItem);
    setAttendanceData(null); // Clear previous data
    console.log('selectedClass updated to:', classItem.name);
  };

  const handleDateRangeChange = (newRange) => {
    console.log('Date range changed to:', newRange);
    setDateRange(newRange);
    if (newRange !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter attendance data based on selected period tab
  const getFilteredAttendanceData = () => {
    if (!attendanceData || !attendanceData.records) {
      return attendanceData;
    }

    // If showing class overview, return all data
    if (selectedAnalyticsTab === 'class') {
      return attendanceData;
    }

    // Filter records for the specific period
    const selectedPeriodId = parseInt(selectedAnalyticsTab);
    const filteredRecords = attendanceData.records.filter(record => {
      return record.periodId === selectedPeriodId || record.period?.id === selectedPeriodId;
    });

    console.log(`Filtered records for period ${selectedPeriodId}:`, filteredRecords.length, 'out of', attendanceData.records.length);

    // Return filtered data with same structure
    return {
      ...attendanceData,
      records: filteredRecords
    };
  };

  // Show loading if tenant context is still loading
  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (loading && !attendanceData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading class tracking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Class Tracking Dashboard</h1>
        <p className="text-gray-600">
          Monitor attendance patterns and analytics for students within each class and period.
        </p>
      </div>

      {/* Class Selection and Date Range Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Class Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Select Class
            </label>
            <select
              value={selectedClass?.id || ''}
              onChange={(e) => {
                console.log('Dropdown onChange triggered with value:', e.target.value, 'type:', typeof e.target.value);
                console.log('Available classes:', availableClasses.map(c => ({ id: c.id, name: c.name, idType: typeof c.id })));
                const classItem = availableClasses.find(c => {
                  const match = String(c.id) === String(e.target.value);
                  console.log(`Comparing ${c.id} (${typeof c.id}) === ${e.target.value} (${typeof e.target.value}): ${match}`);
                  return match;
                });
                console.log('Found classItem:', classItem);
                if (classItem) handleClassSelect(classItem);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={loading}
            >
              <option value="">Select a class...</option>
              {availableClasses.map((classItem) => {
                console.log('Rendering option for class:', classItem.name, 'ID:', classItem.id, 'ID type:', typeof classItem.id);
                return (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name} 
                  </option>
                );
              })}
            </select>
            {/*selectedClass && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <p><span className="font-semibold">Class:</span> {selectedClass.name}</p>
                  <p><span className="font-semibold">Subject:</span> {selectedClass.subject?.name || 'Not specified'}</p>
                  <p><span className="font-semibold">Period:</span> {selectedClass.period?.name || 'Not specified'}</p>
                  {selectedClass.period?.startTime && selectedClass.period?.endTime && (
                    <p><span className="font-semibold">Time:</span> {selectedClass.period.startTime} - {selectedClass.period.endTime}</p>
                  )}
                </div>
              </div>
            )*/}
          </div>

          {/* Date Range Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Date Range
            </label>
            <div className="space-y-3">
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={loading}
              >
                <option value="today">Today</option>
                <option value="thisWeek">This Week (Sunday - Saturday)</option>
                <option value="thisMonth">This Month (1st - Last day)</option>
                <option value="custom">Custom Range</option>
              </select>
              
              {dateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={customDateRange.startDate}
                      onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={customDateRange.endDate}
                      onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Analytics Tabs */}
      {selectedClass && attendanceData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {/* Class Overview Tab */}
              <button
                onClick={() => setSelectedAnalyticsTab('class')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  selectedAnalyticsTab === 'class'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              
              {/* Period Tabs */}
              {availablePeriods.map((period) => (
                <button
                  key={period.id}
                  onClick={() => setSelectedAnalyticsTab(String(period.id))}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    String(selectedAnalyticsTab) === String(period.id)
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {period.name}
                  {period.startTime && period.endTime && (
                    <span className="ml-1 text-xs opacity-75">
                      ({period.startTime}-{period.endTime})
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {attendanceData && (
              <ClassAnalytics
                attendanceData={getFilteredAttendanceData()}
                classData={selectedClass}
                periodData={selectedAnalyticsTab !== 'class' ? availablePeriods.find(p => String(p.id) === String(selectedAnalyticsTab)) : null}
                dateRange={getDateRangeValues()}
              />
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedClass && !loading && (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏫</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Select a Class</h3>
            <p className="text-gray-600 mb-6">
              Choose a class from the dropdown above to view attendance analytics for all students in that class.
            </p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {selectedClass && !attendanceData && !loading && !error && (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Data Available</h3>
            <p className="text-gray-600 mb-6">
              No attendance data found for {selectedClass.name} in the selected date range.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
